import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ToastAction } from "@/components/ui/toast";
import { UserCheck, X } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ImpersonationStatus {
  active: boolean;
  impersonatedUserId?: string;
  impersonatedUserName?: string;
  impersonatedUserEmail?: string;
  impersonatorEmail?: string;
}

export function ImpersonationBanner() {
  const { toast } = useToast();

  const { data: status } = useQuery<ImpersonationStatus>({
    queryKey: ["/api/auth/impersonation-status"],
    refetchInterval: 30000,
  });

  const stopMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/impersonate/stop"),
    onSuccess: () => {
      queryClient.clear();
      toast({ title: "Impersonation stopped", description: "Reloading..." });
      setTimeout(() => window.location.reload(), 600);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Listen for master-admin actions that were blocked because impersonation is active.
  // Only attach the listener while impersonation is actually in progress; there is
  // nothing actionable to show if the session is not active.
  useEffect(() => {
    if (!status?.active) return;

    function handleBlocked() {
      toast({
        title: "Master-admin access blocked",
        description:
          "Master admin actions are disabled while you are impersonating another user. Stop impersonating first.",
        variant: "destructive",
        action: (
          <ToastAction
            altText="Stop impersonating"
            onClick={() => stopMutation.mutate()}
          >
            Stop Impersonating
          </ToastAction>
        ),
      });
    }

    window.addEventListener("master-admin-impersonation-blocked", handleBlocked);
    return () => window.removeEventListener("master-admin-impersonation-blocked", handleBlocked);
  }, [status?.active, toast, stopMutation]);

  if (!status?.active) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-yellow-400 dark:bg-yellow-500 text-black flex items-center justify-between px-4 py-2 shadow-md">
      <div className="flex items-center gap-2 text-sm font-medium">
        <UserCheck className="w-4 h-4 shrink-0" />
        <span>
          Viewing as{" "}
          <strong>{status.impersonatedUserName || status.impersonatedUserEmail}</strong>
          {status.impersonatedUserEmail && status.impersonatedUserName && (
            <span className="font-normal"> ({status.impersonatedUserEmail})</span>
          )}
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="bg-black/10 border-black/20 hover:bg-black/20 text-black text-xs h-7"
        onClick={() => stopMutation.mutate()}
        disabled={stopMutation.isPending}
      >
        <X className="w-3 h-3 mr-1" />
        {stopMutation.isPending ? "Stopping..." : "Stop Impersonating"}
      </Button>
    </div>
  );
}
